import { useCallback, useSyncExternalStore } from "react";

/**
 * App background theme system.
 * The selected theme key is written to `<html data-app-theme="...">` so pure CSS
 * (see index.css) can restyle the `.app-ambient` wrapper used by both the
 * desktop AppShell and the mobile Field View. Persisted per-device in localStorage.
 */
const STORAGE_KEY = "g5-app-theme";

export const DEFAULT_APP_THEME = "architect";

export const APP_THEMES = [
  {
    key: "architect",
    name: "Architect",
    description: "Crisp isometric tower wireframes with warm bronze edge glows - the signature architectural look.",
  },
  {
    key: "energygrid",
    name: "Energy Grid",
    description: "A charged grid — bright pulses racing the gridlines, a glowing registration mark and a charged logo.",
    animated: true,
  },
  {
    key: "skyline",
    name: "Skyline Pulse",
    description: "Night skyline linework with glowing beacons drifting along the rooflines.",
    animated: true,
  },
  {
    key: "beams",
    name: "Structural Flow",
    description: "Steel truss lattice with bronze light tracers running through the members.",
    animated: true,
  },
  {
    key: "contours",
    name: "Contour Glow",
    description: "Flowing survey contours with a slow bronze current travelling the lines.",
    animated: true,
  },
  {
    key: "framework",
    name: "Framework",
    description: "High-detail tower crane and structural frame linework in fine bronze.",
  },
  {
    key: "blueprint",
    name: "Blueprint",
    description: "Sharp drafting grid, floor-plan linework and dimension strings with bronze sweep pulses.",
    animated: true,
  },
  {
    key: "topo",
    name: "Topographic",
    description: "Fine site-survey contours, spot levels and benchmarks with bronze highlights.",
  },
  {
    key: "minimal",
    name: "Minimal",
    description: "Clean warm charcoal with a subtle vignette - no graphics.",
  },
];

const VALID_KEYS = new Set(APP_THEMES.map((t) => t.key));
const listeners = new Set();

let current = DEFAULT_APP_THEME;

const readStored = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID_KEYS.has(v) ? v : DEFAULT_APP_THEME;
  } catch {
    return DEFAULT_APP_THEME;
  }
};

const applyToDom = (key) => {
  document.documentElement.setAttribute("data-app-theme", key);
};

/** Call once on boot (before render) to avoid a theme flash. */
export function initAppTheme() {
  current = readStored();
  applyToDom(current);
}

export function setAppTheme(key) {
  if (!VALID_KEYS.has(key) || key === current) return;
  current = key;
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* private browsing - theme still applies for the session */
  }
  applyToDom(key);
  listeners.forEach((l) => l());
}

const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => current;
const getServerSnapshot = () => DEFAULT_APP_THEME;

export function useAppTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const setTheme = useCallback((k) => setAppTheme(k), []);
  return { theme, setTheme, themes: APP_THEMES };
}
